"use client";

import React, { useEffect, useRef, useState } from "react";
import { LayoutDashboard, Wrench, CalendarClock, Users, Sparkles, Building, CircleCheck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const capabilities = [{
  id: 1,
  title: "IV Therapy",
  description: "Restore balance and combat fatigue with our premium nutrient infusions.",
  iconName: "LayoutDashboard"
}, {
  id: 2,
  title: "HydraFacial Boosters",
  description: "Deeply cleanse and hydrate your skin for a radiant, youthful glow.",
  iconName: "Sparkles"
}, {
  id: 3,
  title: "Immune & Energy",
  description: "Essential vitamins and minerals to supercharge your natural defenses.",
  iconName: "CalendarClock"
}, {
  id: 4,
  title: "Skin Vitality",
  description: "Targeted treatments designed to treat dullness and dehydration.",
  iconName: "Users"
}, {
  id: 5,
  title: "Wellness Lounge",
  description: "Experience rejuvenation in our high-class, luxurious environment.",
  iconName: "Building"
}, {
  id: 6,
  title: "Expert Care",
  description: "Personalized treatment plans delivered by healthcare professionals.",
  iconName: "Wrench"
}];
const capabilityFeatures = [{
  capabilityId: 1,
  text: "Customized vitamin blends"
}, {
  capabilityId: 1,
  text: "Rapid hydration recovery"
}, {
  capabilityId: 1,
  text: "Electrolyte replenishment"
}, {
  capabilityId: 2,
  text: "Exfoliating skin resurfacing"
}, {
  capabilityId: 2,
  text: "Deep pore detoxification"
}, {
  capabilityId: 2,
  text: "Antioxidant infusion"
}, {
  capabilityId: 3,
  text: "B12 & Vitamin C boosts"
}, {
  capabilityId: 3,
  text: "Metabolic support"
}, {
  capabilityId: 3,
  text: "Natural energy elevation"
}, {
  capabilityId: 4,
  text: "Collagen stimulation"
}, {
  capabilityId: 4,
  text: "Fine line reduction"
}, {
  capabilityId: 4,
  text: "Brightening treatments"
}, {
  capabilityId: 5,
  text: "Private treatment suites"
}, {
  capabilityId: 5,
  text: "Serene spa atmosphere"
}, {
  capabilityId: 5,
  text: "Premium amenities"
}, {
  capabilityId: 6,
  text: "Clinical precision"
}, {
  capabilityId: 6,
  text: "Safety-first protocols"
}, {
  capabilityId: 6,
  text: "Holistic health focus"
}];
const iconMap: Record<string, React.ElementType> = {
  LayoutDashboard,
  Wrench,
  CalendarClock,
  Users,
  Sparkles,
  Building
};
function SpotlightCard({
  children,
  className = "",
  index
}: {
  children: React.ReactNode;
  className?: string;
  index: number;
}) {
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty("--mouse-x", `${x}px`);
    e.currentTarget.style.setProperty("--mouse-y", `${y}px`);
  };
  return <div data-index={index} onMouseMove={handleMouseMove} className={`group relative overflow-hidden rounded-2xl bg-card/30 backdrop-blur-xl border border-[#D4AF37] transition-all duration-500 hover:scale-[1.02] hover:bg-secondary/10 shadow-xl shadow-black/20 hover:shadow-[0_0_20px_rgba(212,175,55,0.2)] ${className}`}>
      <div className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition duration-500 group-hover:opacity-100 z-0" style={{
      background: `radial-gradient(600px circle at var(--mouse-x, 0) var(--mouse-y, 0), rgba(212, 175, 55, 0.15), transparent 40%)`
    }} />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>;
}
export default function CorePlatformCapabilities() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224579" id="elite-hydration-skin-services" className="bg-background text-foreground py-24 px-4 overflow-hidden relative">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-96 bg-primary/10 blur-[120px] rounded-full pointer-events-none opacity-30" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center justify-center px-4 py-1.5 mb-6 text-xs font-bold tracking-widest uppercase rounded-full bg-primary/10 text-primary border border-primary/30">
            Premium Wellness
          </div>
          <h2 className="text-4xl md:text-6xl font-bold font-serif text-foreground mb-6 tracking-tight">Elite Hydration IV Services &amp; Therapy</h2>
          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            Experience the pinnacle of rejuvenation with our curated selection of IV therapies and advanced skin treatments designed for the modern lifestyle.
          </p>
        </div>

        <div ref={gridRef} className={`flex overflow-x-auto pb-8 snap-x snap-mandatory md:grid md:grid-cols-2 lg:grid-cols-3 gap-10 transition-all duration-1000 delay-200 transform ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {capabilities.map((capability, i) => {
          const IconComponent = iconMap[capability.iconName];
          const cardFeatures = capabilityFeatures.filter(f => f.capabilityId === capability.id);
          return <div key={capability.id} data-index={i} className="min-w-[85vw] md:min-w-0 snap-center h-full">
                <SpotlightCard index={i} className="h-full">
                  <Card className="bg-transparent border-none shadow-none h-full flex flex-col p-6">
                    <CardHeader className="pb-4 px-0">
                      <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary/5 border border-[#D4AF37]/40 relative group-hover:animate-pulse transition-transform duration-500">
                        <IconComponent className="size-7 text-[#D4AF37] absolute opacity-40 blur-[3px]" strokeWidth={1.5} />
                        <IconComponent className="size-7 text-[#D4AF37] relative z-10" strokeWidth={1.5} />
                      </div>
                      <CardTitle className="text-2xl font-bold font-serif text-foreground mb-2">
                        {capability.title}
                      </CardTitle>
                      <CardDescription className="text-base text-muted-foreground font-default">
                        {capability.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="mt-auto pt-4 px-0">
                      <ul className="space-y-3 mb-6">
                        {cardFeatures.map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-sm text-muted-foreground font-default min-h-[44px] items-center">
                            <CircleCheck className="size-5 text-[#D4AF37] shrink-0" />
                            <span>{feature.text}</span>
                          </li>)}
                      </ul>
                      <div className="group/link flex items-center gap-2 text-sm font-bold text-primary cursor-pointer transition-colors hover:text-primary/80 min-h-[44px]">
                        
                        
                      </div>
                    </CardContent>
                  </Card>
                </SpotlightCard>
              </div>;
        })}
        </div>
      </div>
    </section>;
}